import omitBy from 'lodash/omitBy';
import isUndefined from 'lodash/isUndefined';
import AppStorage from '@Services/AppStorage';
import {
  ADDTOBUILDER_NODES,
  FETCH_NODES_LIST,
  FETCH_FORMS,
  FETCH_HISTORY_ITEMS,
  RENAME_GROUP_TITLE,
  UPDATE_INDIVIDUAL_NODE,
  UPDATE_NODES,
  CLEANUP_ON_UNMOUNT,
  UPDATE_CHILDREN_VALUE,
  GET_IMPORTED_QUERY,
  UPDATE_FORMS,
  CLEAR_NODES_LIST_UPDATE_HIDDEN_LIST,
  MERGE_DRAG_NODES,
  SET_SEARCH_SECTION_PANEL_STATE,
  SEARCH_SECTION_TABS,
} from './QueryletConstants';
import * as qbReducerHelper from './QueryletReducerHelper';

export const initialState = {
  nodesList: [],
  properties: [],
  favList: [],
  forms: [],
  hiddenList: [],
  historyItems: [],
  indexedQuerylets: {}, // For searching fields in the right section
  indexedForms: {}, // For searching forms in the right section
  importedQuery: null,
  searchSectionPanelState: {
    openedTab: SEARCH_SECTION_TABS[0],
    openedPanels: [],
    searchValue: '',
  },
};

const updateState = (state, newValues) => ({ ...state, ...newValues });
// Helper function to recursively update nested items
const updateNestedItems = (items, payload) => {
  if (!items || !Array.isArray(items)) return items;

  return items.map((item) => {
    // Check if this item matches the target UUID
    if (item.uuid && payload.uuid && item.uuid === payload.uuid) {
      return { ...item, ...payload.data };
    }

    // If this item has nested items, recurse into them
    if (item.items && Array.isArray(item.items)) {
      return {
        ...item,
        items: updateNestedItems(item.items, payload),
      };
    }

    // If this item has children, check them too
    if (item.children && Array.isArray(item.children)) {
      return {
        ...item,
        children: updateNestedItems(item.children, payload),
      };
    }

    return item;
  });
};

const updateIndividualNode = (state, payload) => ({
  ...state,
  nodesList: updateNestedItems(state.nodesList, payload),
});

function updateItems(items, actionPayload, idoruuid) {
  return items.map((item) => {
    if (
      item.id !== 'wrapper' &&
      ((item.uuid && item.uuid === actionPayload.nodeId) || (!item.uuid && item.id === actionPayload.parentId))
    ) {
      return {
        ...item,
        children: (item.children || []).map((child) => (child[idoruuid] === actionPayload.childId ? { ...actionPayload.child } : child)),
      };
    }
    if (item.id === 'wrapper') {
      return {
        ...item,
        items: updateItems(item.items || [], actionPayload, idoruuid),
      };
    }
    // If there can be nested items in any item, recurse here too
    return {
      ...item,
      items: item.items ? updateItems(item.items, actionPayload, idoruuid) : item.items,
    };
  });
}

const updatebyIdorUUID = (state, actionPayload, idoruuid) => ({
  ...state,
  nodesList: state.nodesList?.map((node) => {
    if (
      node.id !== 'wrapper' &&
      ((node.uuid && node.uuid === actionPayload.nodeId) || (!node.uuid && node.id === actionPayload.parentId))
    ) {
      return {
        ...node,
        children: node.children?.map((child) => (child[idoruuid] === actionPayload.childId ? { ...child, ...actionPayload.child } : child)),
      };
    }
    // Usage in your return statement:
    return {
      ...node,
      items: node.items ? updateItems(node.items, actionPayload, idoruuid) : node.items,
    };
  }),
});

// eslint-disable-next-line default-param-last
export default function queryletReducer(state = initialState, action) {
  const { type, payload } = action;

  switch (type) {
    case RENAME_GROUP_TITLE: {
      return updateState(state, { groupedItem: payload.groupedItem });
    }
    case FETCH_NODES_LIST: {
      return {
        ...state,
        nodesList: payload.nodesList,
        properties: payload.properties,
        favList: payload.favList,
        indexedQuerylets: qbReducerHelper.indexQuerylets(payload.properties),
      };
    }
    case FETCH_FORMS: {
      return { ...state, forms: payload.forms, indexedForms: qbReducerHelper.indexForms(payload.forms, state.indexedQuerylets) };
    }
    case FETCH_HISTORY_ITEMS: {
      return updateState(state, { historyItems: payload.historyItems });
    }
    case GET_IMPORTED_QUERY: {
      const updatedState = updateState(state, { importedQuery: payload.importedQuery });
      AppStorage.instance.set('qbNodesList', updatedState.nodesList);
      return updatedState;
    }
    case ADDTOBUILDER_NODES: {
      const updatedState = { ...state, nodesList: [...state.nodesList, ...payload.nodesList] };
      AppStorage.instance.set('qbNodesList', updatedState.nodesList);

      return updatedState;
    }

    case UPDATE_FORMS: {
      return updateState(state, { forms: [...payload.forms] });
    }
    case UPDATE_INDIVIDUAL_NODE: {
      /** this additional check is needed for forms as forms items id will match node value so looping over items for forms */
      const updatedState = updateIndividualNode(state, payload);
      AppStorage.instance.set('qbNodesList', updatedState.nodesList);
      return updatedState;
    }
    case UPDATE_NODES: {
      const updatedState = { ...state, nodesList: [...payload.nodesList] };

      AppStorage.instance.set('qbNodesList', updatedState.nodesList);

      return updatedState;
    }
    case MERGE_DRAG_NODES: {
      const updatedState = { ...state, nodesList: [...payload.updatedObj] };

      AppStorage.instance.set('qbNodesList', updatedState.nodesList);

      return updatedState;
    }
    case UPDATE_CHILDREN_VALUE: {
      /** this additional check is needed for forms as forms items id will match node value so looping over items for forms */
      const idoruuid = action.payload?.isuuid ? 'uuid' : 'id';
      const updatedState = updatebyIdorUUID(state, action.payload, idoruuid);
      AppStorage.instance.set('qbNodesList', updatedState.nodesList);
      return updatedState;
    }
    case CLEAR_NODES_LIST_UPDATE_HIDDEN_LIST:
      return { ...state, nodesList: [], hiddenList: [...payload.hiddenList] };
    case SET_SEARCH_SECTION_PANEL_STATE: {
      return {
        ...state,
        searchSectionPanelState: {
          ...state.searchSectionPanelState,
          ...omitBy(payload, isUndefined),
        },
      };
    }
    case CLEANUP_ON_UNMOUNT:
      // Don't clean opened search section on unmount
      return { ...initialState, searchSectionPanelState: state.searchSectionPanelState };
    default: {
      return state;
    }
  }
}
